import { Component, Input } from "@angular/core";
import { LandInput, Project } from "../../drc-valuation/types";
import { ComponentStore } from "@ngrx/component-store";
import { combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { mapLevel } from "../../drc-valuation/utils/level";
import { depreciation, getDepreciationOptions } from "../../drc-valuation/utils/calculation/calculation";
import { UnitService } from "../../drc-valuation/utils/unit.service";

interface State {
  level?: number
  valuationDate?: Date
  landInput?: LandInput
  currency?: string
  unitAreaMeasurementId?: number
  projects: Project[]
}

@Component({
  selector: 'app-drc-summary',
  templateUrl: './drc-summary.component.html',
  styleUrls: ['./drc-summary.component.scss'],
  providers: [ComponentStore]
})
export class DRCSummaryComponent {
  @Input()
  set level(value: number) {
    this.componentStore.patchState({level: value})
  }

  @Input()
  set valuationDate(value: Date) {
    this.componentStore.patchState({valuationDate: value})
  }

  @Input()
  set landInput(value: LandInput) {
    this.componentStore.patchState({landInput: value})
  }

  @Input()
  set currency(value: string) {
    this.componentStore.patchState({currency: value})
  }

  @Input()
  set projects(value: Project[]) {
    this.componentStore.patchState({projects: value})
  }

  @Input()
  set unitAreaMeasurementId(value: number) {
    this.componentStore.patchState({unitAreaMeasurementId: value})
  }

  private _level$ = this.componentStore.select(state => state.level)
  private _valuationDate$ = this.componentStore.select(state => state.valuationDate)
  private _landInput$ = this.componentStore.select(state => state.landInput)
  private _currency$ = this.componentStore.select(state => state.currency)
  private _projects$ = this.componentStore.select(state => state.projects)
  private _unitAreaMeasurementId$ = this.componentStore.select(state => state.unitAreaMeasurementId)

  private _unitOptions = this.unitService.getOptions()

  viewModel$ = combineLatest([
    this._level$,
    this._valuationDate$,
    this._landInput$,
    this._currency$,
    this._projects$,
    this._unitAreaMeasurementId$
  ]).pipe(
    map(([level, valuationDate, landInput, currency, projects, unitAreaMeasurementId]) => {
      let usedLandInput = false
      const calculatedProjects = projects.map(item => {
        if (item.useLandValueInput) {
          usedLandInput = true
        }
        const options = getDepreciationOptions(
          item.depreciationMethod,
          item.sCurveSteepness,
          item.economicLife,
          item.salvageValue,
          item.depreciationFactorPerYear,
          item.depreciationLevel,
          'DRC Summary',
          item.physicalDepreciationFactor,
          item.functionalDepreciation,
          item.economicDepreciation,
          item.richardsSteepness,
          item.richardsInflection,
          item.richardsShape,
          item.weibullShape,
          item.gompertzA,
          item.gompertzB
        )
        const result = depreciation(
          item.records,
          {
            valuationDate: valuationDate,
            landInput,
            buildingConstructionDate: item.buildingConstructionDate,
            useLandValueInput: item.useLandValueInput,
            useSizeAsQuantity: item.useSizeAsQuantity,
            size: item.size,
            unitAreaMeasurementId,
            level
          },
          options
        )
        if (!result) return null
        const {records, totalCost: cost, totalDepreciatedCost: depreciated_cost} = result
        return {
          index: item.index,
          records: records.map(item => {
            if (item.kind === 'header') return item;
            return {
              ...item,
              unit_selected: this._selectedUnit(item.unit)
            }
          }),
          total: {
            cost,
            depreciated_cost,
            depreciated_per_unit: depreciated_cost / item.size
          }
        }
      })

      const landInputTotal = landInput ? landInput.size * landInput.price : 0
      const total = calculatedProjects
        .filter(item => item !== null)
        .reduce((acc, item) => {
          return acc + item.total.depreciated_cost
        }, !usedLandInput ? landInputTotal : 0)

      return {
        currency,
        level: mapLevel(level),
        projects: calculatedProjects,
        usedLandInput,
        landInputTotal: landInputTotal,
        total
      }
    })
  )

  constructor(
    private componentStore: ComponentStore<State>,
    private unitService: UnitService
  ) {
    this.componentStore.setState({
      projects: []
    })
  }

  identity(index: number, project: unknown) {
    return index
  }

  private _selectedUnit(unit: number) {
    const item = this._unitOptions.find(x => x.value === unit)
    return item ? item.label : 'N/A'
  }
}